import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ResultView } from './Result';
import PhoneOtpVerify from '../../components/PhoneOtpVerify';

const WARN_LIMIT = 3;

// Light client-side pre-check for instant feedback only — NOT authoritative.
// The server holds the real, admin-editable blocklist and re-checks on submit.
const CLIENT_JUNK_NAMES = ['xyz', 'test', 'asdf', 'abcd', 'abc', 'bro', 'broo', 'fake', 'dummy', 'na', 'none'];
const CLIENT_PROFANITY = ['porn', 'fuck', 'bitch', 'whore', 'slut', 'asshole', 'bullshit', 'khanki', 'chuda', 'choda', 'gandu', 'harami'];
const LEET_MAP = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '@': 'a' };
function looksLikeBadName(name) {
  const basic = String(name || '').toLowerCase().trim().replace(/\s+/g, ' ');
  const stripped = basic.replace(/[01345@]/g, ch => LEET_MAP[ch]).replace(/[\s.\-_\d]/g, '');
  if (CLIENT_JUNK_NAMES.includes(basic)) return true;
  return CLIENT_PROFANITY.some(w => stripped.includes(w));
}

function MathText({ text }) {
  const ref = useRef();
  useEffect(() => {
    if (!ref.current || !text) return;
    const parts = text.split(/(\$[^$]+\$)/g);
    ref.current.innerHTML = parts.map(p => {
      if (p.startsWith('$') && p.endsWith('$')) {
        try {
          if (window.katex) return window.katex.renderToString(p.slice(1,-1), { throwOnError: false });
        } catch {}
      }
      return p.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    }).join('');
  }, [text]);
  return <span ref={ref} className="math-text" />;
}

export default function StudentExam() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading, info, register, exam, result, error
  const [examInfo, setExamInfo] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const [regForm, setRegForm] = useState({ student_name: '', student_phone: '', parent_phone: '', roll_number: '' });
  const [otpIdToken, setOtpIdToken] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [started, setStarted] = useState(null);
  const [warnings, setWarnings] = useState(0);
  const [result, setResult] = useState(null);
  const [reviewDetails, setReviewDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef();
  const [settings, setSettings] = useState({ brand_name: 'Oexams', tagline: '' });
  const [watermark, setWatermark] = useState('');
  const [countdown, setCountdown] = useState(null); // seconds until exam starts
  const countdownRef = useRef();
  const [checkResultForm, setCheckResultForm] = useState({ student_name: '', student_phone: '' });
  const [checkResultData, setCheckResultData] = useState(null);
  const [checkResultError, setCheckResultError] = useState('');
  const [checkingResult, setCheckingResult] = useState(false);

  useEffect(() => {
    API.get('/admin/settings').then(r => setSettings(r.data)).catch(() => {});
    API.get(`/student/exam/${token}`).then(r => {
      setExamInfo(r.data); setPhase('info');
    }).catch(err => { setErrMsg(err.response?.data?.error || 'Exam not found'); setPhase('error'); });
  }, [token]);

  // Countdown timer for scheduled exams
  useEffect(() => {
    if (phase !== 'info' || !examInfo?.start_time) return;
    const tick = () => {
      const diff = Math.ceil((new Date(examInfo.start_time).getTime() - Date.now()) / 1000);
      if (diff <= 0) { setCountdown(0); clearInterval(countdownRef.current); }
      else setCountdown(diff);
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => clearInterval(countdownRef.current);
  }, [phase, examInfo]);

  const startExam = async () => {
    if (!regForm.student_name || !regForm.student_phone) return toast.error('Name and phone required');
    if (looksLikeBadName(regForm.student_name)) return toast.error('Please enter your real full name.');
    if (examInfo.mode === 'school' && !regForm.roll_number) return toast.error('Roll number is required');
    if (examInfo.require_otp && !otpIdToken) return toast.error('Please verify your phone number first');
    try {
      const r = await API.post(`/student/exam/${token}/start`, { ...regForm, idToken: otpIdToken });
      setAttemptId(r.data.attempt_id);
      setQuestions(r.data.questions);
      setAnswers(r.data.existing_answers || {});
      const secs = r.data.duration_minutes * 60;
      if (r.data.existing_answers && r.data.started_at) {
        const elapsed = Math.floor((Date.now() - new Date(r.data.started_at).getTime()) / 1000);
        setTimeLeft(Math.max(0, secs - elapsed));
      } else { setTimeLeft(secs); }
      setStarted(new Date(r.data.started_at || Date.now()));
      setWatermark(`${regForm.student_name} | ${regForm.student_phone}`);
      setPhase('exam');
      enterFullscreen();
    } catch (err) { toast.error(err.response?.data?.error || 'Error starting exam'); }
  };

  const checkMyResult = async () => {
    if (!checkResultForm.student_name || !checkResultForm.student_phone) return toast.error('Name and phone required');
    setCheckingResult(true);
    setCheckResultError('');
    setCheckResultData(null);
    try {
      const r = await API.post(`/student/exam/${token}/my-result`, checkResultForm);
      setCheckResultData(r.data);
    } catch (err) {
      setCheckResultError(err.response?.data?.error || 'Failed to fetch result');
    } finally {
      setCheckingResult(false);
    }
  };

  const enterFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  // Timer
  useEffect(() => {
    if (phase !== 'exam') return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); submitExam(true); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // Anti-cheat: tab switch / visibility
  const handleVisibility = useCallback(() => {
    if (phase !== 'exam') return;
    if (document.hidden) { recordWarning(); }
  }, [phase]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [handleVisibility]);

  // Anti-cheat: fullscreen exit
  useEffect(() => {
    const handle = () => {
      if (phase === 'exam' && !document.fullscreenElement) {
        recordWarning();
        enterFullscreen();
      }
    };
    document.addEventListener('fullscreenchange', handle);
    return () => document.removeEventListener('fullscreenchange', handle);
  }, [phase]);

  const recordWarning = async () => {
    if (!attemptId) return;
    try {
      const r = await API.post(`/student/attempt/${attemptId}/warning`);
      const w = r.data.warnings;
      setWarnings(w);
      if (w >= WARN_LIMIT) {
        toast.error('⚠️ Exam auto-submitted due to cheating violations!');
        clearInterval(timerRef.current);
        submitExam(true);
      } else {
        toast.error(`⚠️ Warning ${w}/${WARN_LIMIT}: Don't switch tabs or exit fullscreen!`);
      }
    } catch {}
  };

  const selectAnswer = async (qid, opt, type) => {
    let updated;
    if (type === 'multiple') {
      const prev = answers[qid] || [];
      updated = prev.includes(opt) ? prev.filter(x => x !== opt) : [...prev, opt];
    } else { updated = [opt]; }
    setAnswers(a => ({ ...a, [qid]: updated }));
    try { await API.post(`/student/attempt/${attemptId}/answer`, { question_id: qid, selected_options: updated }); } catch {}
  };

  const submitExam = async (auto = false) => {
    if (submitting) return;
    if (!auto && !window.confirm('Are you sure you want to submit the exam?')) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    try {
      const timeUsed = started ? Math.floor((Date.now() - new Date(started).getTime()) / 1000) : 0;
      const r = await API.post(`/student/attempt/${attemptId}/submit`, { time_used: timeUsed, auto_submitted: auto });
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      setResult(r.data); setPhase('result');
      // Fetch explanations/answer review, if the teacher has enabled result viewing
      if (r.data.allow_result_view) {
        try {
          const rr = await API.get(`/student/attempt/${attemptId}/result`);
          setReviewDetails(rr.data);
        } catch {}
      }
    } catch (err) { toast.error('Submission failed'); setSubmitting(false); }
  };

  const downloadResultPDF = () => {
    const doc = new jsPDF();
    const brand = settings.brand_name || 'Oexams';
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text(brand, 105, 15, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(settings.tagline || '', 105, 21, { align: 'center' });
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text(`Exam Result`, 105, 32, { align: 'center' });
    doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text(`Exam: ${examInfo.title}`, 14, 44);
    doc.text(`Student: ${regForm.student_name}`, 14, 52);
    doc.text(`Phone: ${regForm.student_phone}`, 14, 60);
    if (regForm.roll_number) doc.text(`Roll No: ${regForm.roll_number}`, 14, 68);
    autoTable(doc, {
      startY: 76,
      head: [['', 'Details']],
      body: [
        ['Score', `${result.score} / ${result.total_marks}`],
        ['Correct Answers', result.correct_count],
        ['Incorrect Answers', result.incorrect_count],
        ['Percentage', `${result.percentage}%`],
      ],
      styles: { fontSize: 11 },
      headStyles: { fillColor: [99, 102, 241] },
    });
    const y = doc.lastAutoTable.finalY + 16;
    doc.setFontSize(9); doc.setTextColor(150);
    doc.text(`Powered by ${brand} • ${settings.tagline || ''}`, 105, y, { align: 'center' });
    doc.save(`${regForm.student_name}_result.pdf`);
  };

  const fmtCountdown = s => {
    if (s <= 0) return null;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m ${String(sec).padStart(2,'0')}s`;
    return `${String(m).padStart(2,'0')}m ${String(sec).padStart(2,'0')}s`;
  };
  const examNotStartedYet = examInfo?.start_time && countdown !== null && countdown > 0;
  const fmt = s => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
  const answered = Object.keys(answers).filter(k => answers[k]?.length > 0).length;

  if (phase === 'loading') return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh' }}><div className="spinner"/></div>;

  if (phase === 'error') return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',flexDirection:'column',gap:16,padding:20 }}>
      <div style={{ fontSize: 64 }}>❌</div>
      <h2>{errMsg}</h2>
      <p className="text-muted">Please check the exam link or contact your teacher.</p>
    </div>
  );

  if (phase === 'info') return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20,background:'radial-gradient(ellipse at 50% 20%, rgba(99,102,241,0.2) 0%, transparent 60%), var(--bg)' }}>
      <div style={{ maxWidth:480,width:'100%' }}>
        <div style={{ textAlign:'center',marginBottom:28 }}>
          <div style={{ fontSize:48,marginBottom:8 }}>📝</div>
          <h1 style={{ fontSize:26,fontWeight:800 }}>{examInfo.title}</h1>
          {examInfo.subject && <p style={{ color:'var(--text2)',marginTop:4 }}>Subject: {examInfo.subject}</p>}
          <p style={{ color:'var(--text2)',fontSize:14 }}>by {examInfo.teacher_name}</p>
        </div>
        <div className="card">
          <div style={{ display:'flex',gap:12,flexWrap:'wrap',marginBottom:20 }}>
            <span className="badge badge-info">⏱️ Duration: {examInfo.duration_minutes} minutes</span>
            {examInfo.end_time && <span className="badge badge-danger">🕐 Ends: {new Date(examInfo.end_time).toLocaleString()}</span>}
          </div>
          {/* Scheduled exam countdown */}
          {examInfo.start_time && (
            examNotStartedYet ? (
              <div style={{ background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:12, padding:'20px 24px', marginBottom:20, textAlign:'center' }}>
                <div style={{ fontSize:13, color:'#fcd34d', fontWeight:600, marginBottom:8 }}>🕐 Exam starts at {new Date(examInfo.start_time).toLocaleString()}</div>
                <div style={{ fontSize:36, fontWeight:800, fontFamily:'var(--mono)', color:'#f59e0b', letterSpacing:2 }}>{fmtCountdown(countdown)}</div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>The Start button will unlock automatically when the exam opens</div>
              </div>
            ) : (
              <div style={{ background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.3)', borderRadius:8, padding:'10px 16px', marginBottom:16, fontSize:13, color:'#34d399', fontWeight:600, textAlign:'center' }}>
                ✅ Exam is now open!
              </div>
            )
          )}
          {examInfo.instructions && <div className="alert alert-info" style={{ marginBottom:20,fontSize:13 }}><strong>Instructions:</strong> {examInfo.instructions}</div>}
          <div className="alert alert-warning" style={{ marginBottom:20,fontSize:12 }}>
            ⚠️ <strong>Anti-Cheat Active:</strong> The exam will run in fullscreen. Switching tabs or exiting fullscreen will trigger warnings. After {WARN_LIMIT} warnings, your exam will be auto-submitted.
          </div>
          <button className="btn btn-primary btn-full btn-lg" onClick={() => setPhase('register')} disabled={examNotStartedYet} style={examNotStartedYet ? { opacity:0.5, cursor:'not-allowed' } : {}}>
            {examNotStartedYet ? `🔒 Opens in ${fmtCountdown(countdown)}` : '📋 Register & Start'}
          </button>
          <div style={{ textAlign:'center', marginTop:14 }}>
            <button
              onClick={() => { setCheckResultError(''); setCheckResultData(null); setPhase('check-result'); }}
              style={{ background:'none', border:'none', color:'var(--primary-light)', fontSize:13, cursor:'pointer', textDecoration:'underline' }}
            >
              Already took this exam? Check your result
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (phase === 'check-result') {
    if (checkResultData) return (
      <div style={{ minHeight:'100vh', padding:'20px 0', background:'var(--bg)' }}>
        <div style={{ maxWidth:480, margin:'0 auto 8px', padding:'0 20px' }}>
          <button className="btn btn-secondary" onClick={() => { setCheckResultData(null); setCheckResultForm({ student_name:'', student_phone:'' }); }}>← Check another result</button>
        </div>
        <ResultView data={checkResultData} />
      </div>
    );
    return (
      <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20,background:'var(--bg)' }}>
        <div style={{ maxWidth:440,width:'100%' }}>
          <div className="card">
            <h2 style={{ fontWeight:700,marginBottom:20 }}>🔍 Check Your Result</h2>
            <p style={{ color:'var(--text2)',fontSize:13,marginBottom:20 }}>Exam: <strong>{examInfo.title}</strong></p>
            <div className="form-group"><label>Full Name *</label><input value={checkResultForm.student_name} onChange={e => setCheckResultForm(f=>({...f,student_name:e.target.value}))} placeholder="Name you used during the exam" /></div>
            <div className="form-group"><label>Phone Number *</label><input type="tel" value={checkResultForm.student_phone} onChange={e => setCheckResultForm(f=>({...f,student_phone:e.target.value}))} placeholder="Phone you used during the exam" /></div>
            {checkResultError && <div className="alert alert-danger" style={{ marginBottom:16,fontSize:13 }}>{checkResultError}</div>}
            <button className="btn btn-primary btn-full btn-lg" onClick={checkMyResult} disabled={checkingResult}>{checkingResult ? 'Checking...' : '🔍 Check Result'}</button>
            <button className="btn btn-secondary btn-full" style={{ marginTop:8 }} onClick={() => { setCheckResultError(''); setPhase('info'); }}>← Back</button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'register') return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20,background:'var(--bg)' }}>
      <div style={{ maxWidth:440,width:'100%' }}>
        <div className="card">
          <h2 style={{ fontWeight:700,marginBottom:20 }}>📋 Student Registration</h2>
          <p style={{ color:'var(--text2)',fontSize:13,marginBottom:20 }}>Exam: <strong>{examInfo.title}</strong></p>
          <div className="form-group"><label>Full Name *</label><input value={regForm.student_name} onChange={e => setRegForm(f=>({...f,student_name:e.target.value}))} placeholder="Your full name" /></div>
          <div className="form-group"><label>Phone Number *</label><input type="tel" value={regForm.student_phone} onChange={e => { setRegForm(f=>({...f,student_phone:e.target.value})); setOtpIdToken(null); }} placeholder="Your phone number" /></div>
          <div className="form-group"><label>Parent's Phone (optional)</label><input type="tel" value={regForm.parent_phone} onChange={e => setRegForm(f=>({...f,parent_phone:e.target.value}))} placeholder="Parent's phone number" /></div>
          <div className="form-group"><label>Roll Number {examInfo.mode==='school' ? '*' : '(optional)'}</label><input value={regForm.roll_number} onChange={e => setRegForm(f=>({...f,roll_number:e.target.value}))} placeholder="Roll number" /></div>
          {examInfo.require_otp && /^[0-9]{10}$/.test(regForm.student_phone) && (
            <PhoneOtpVerify phone={regForm.student_phone} verified={!!otpIdToken} onVerified={setOtpIdToken} />
          )}
          <button className="btn btn-primary btn-full btn-lg" onClick={startExam}>🚀 Start Exam</button>
          <button className="btn btn-secondary btn-full" style={{ marginTop:8 }} onClick={() => setPhase('info')}>← Back</button>
        </div>
      </div>
    </div>
  );

  if (phase === 'exam') {
    const q = questions[currentQ];
    return (
      <div style={{ minHeight:'100vh',background:'var(--bg)',position:'relative',userSelect:'none' }}>
        {/* Watermark */}
        <div style={{ position:'fixed',inset:0,display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none',zIndex:1,opacity:0.06,transform:'rotate(-30deg)',fontSize:28,fontWeight:800,color:'white',letterSpacing:2,whiteSpace:'nowrap' }}>
          {watermark} • {watermark} • {watermark}
        </div>

        {/* Top bar */}
        <div style={{ position:'sticky',top:0,zIndex:10,background:'var(--bg2)',borderBottom:'1px solid var(--border)',padding:'10px 16px' }}>
          <div style={{ maxWidth:900,margin:'0 auto',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8 }}>
            <div>
              <div style={{ fontWeight:700,fontSize:15 }}>{examInfo.title}</div>
              <div style={{ fontSize:12,color:'var(--text2)' }}>{regForm.student_name} • Q{currentQ+1}/{questions.length}</div>
            </div>
            <div style={{ display:'flex',alignItems:'center',gap:12 }}>
              {warnings > 0 && <span className="badge badge-danger">⚠️ {warnings}/{WARN_LIMIT} warnings</span>}
              <div style={{ background: timeLeft < 60 ? 'rgba(239,68,68,0.2)' : 'var(--card2)', border:`1px solid ${timeLeft < 60 ? 'var(--danger)' : 'var(--border)'}`, padding:'6px 14px', borderRadius:8, fontFamily:'var(--mono)', fontWeight:700, fontSize:18, color: timeLeft < 60 ? 'var(--danger)' : 'var(--text)' }}>
                ⏱️ {fmt(timeLeft)}
              </div>
              <span style={{ fontSize:13,color:'var(--text2)' }}>{answered}/{questions.length} answered</span>
              <button onClick={() => submitExam(false)} className="btn btn-danger btn-sm" disabled={submitting}>Submit</button>
            </div>
          </div>
        </div>

        <div className="exam-layout" style={{ maxWidth:900,margin:'0 auto',padding:'20px 16px',display:'flex',gap:16 }}>
          {/* Question nav panel */}
          <div className="exam-nav-panel" style={{ width:180,flexShrink:0 }}>
            <div className="exam-nav-inner" style={{ background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,padding:12,position:'sticky',top:70 }}>
              <div style={{ fontSize:12,fontWeight:700,color:'var(--text2)',marginBottom:10 }}>QUESTIONS</div>
              <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4 }}>
                {questions.map((qq,i) => (
                  <button key={i} onClick={() => setCurrentQ(i)} style={{
                    padding:'6px 4px',borderRadius:6,border:'1px solid',cursor:'pointer',fontSize:12,fontWeight:700,
                    background: answers[qq.id]?.length > 0 ? 'var(--success)' : currentQ===i ? 'var(--primary)' : 'var(--card2)',
                    color: (answers[qq.id]?.length > 0 || currentQ===i) ? 'white' : 'var(--text2)',
                    borderColor: answers[qq.id]?.length > 0 ? 'var(--success)' : currentQ===i ? 'var(--primary)' : 'var(--border)',
                  }}>{i+1}</button>
                ))}
              </div>
              <div style={{ marginTop:12,fontSize:11,color:'var(--text3)' }}>
                <div style={{ display:'flex',gap:6,alignItems:'center',marginBottom:4 }}><span style={{ width:10,height:10,background:'var(--success)',borderRadius:2,display:'inline-block' }}/> Answered</div>
                <div style={{ display:'flex',gap:6,alignItems:'center' }}><span style={{ width:10,height:10,background:'var(--card2)',border:'1px solid var(--border)',borderRadius:2,display:'inline-block' }}/> Not answered</div>
              </div>
            </div>
          </div>

          {/* Question card */}
          {q && (
            <div className="exam-question-panel" style={{ flex:1, minWidth:0 }}>
              <div className="card">
                <div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:16,flexWrap:'wrap' }}>
                  <span style={{ background:'var(--primary)',color:'white',padding:'4px 12px',borderRadius:20,fontWeight:700,fontSize:13 }}>Q{currentQ+1}</span>
                  <span className={`badge ${q.question_type==='multiple'?'badge-warning':'badge-info'}`}>{q.question_type==='multiple'?'Multiple correct':'Single correct'}</span>
                  <span className="badge badge-success">{q.marks} mark{q.marks>1?'s':''}</span>
                </div>
                <div style={{ fontSize:17,fontWeight:500,lineHeight:1.6,marginBottom:20,overflowWrap:'break-word',wordBreak:'break-word' }}><MathText text={q.question_text} /></div>
                {q.question_image && <img src={q.question_image} alt="question" style={{ maxWidth:'100%',maxHeight:200,borderRadius:8,marginBottom:16,objectFit:'contain' }} />}
                <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
                  {q.options.map((opt,j) => {
                    const selected = answers[q.id]?.includes(opt);
                    return (
                      <button key={j} onClick={() => selectAnswer(q.id, opt, q.question_type)} style={{
                        display:'flex',alignItems:'center',gap:12,padding:'12px 16px',
                        borderRadius:10,border:`2px solid ${selected?'var(--primary)':'var(--border)'}`,
                        background: selected?'rgba(99,102,241,0.15)':'var(--card2)',
                        cursor:'pointer',textAlign:'left',color:'var(--text)',transition:'all 0.15s',
                        width:'100%',maxWidth:'100%',boxSizing:'border-box'
                      }}>
                        <span style={{ width:28,height:28,borderRadius:q.question_type==='multiple'?4:'50%',border:`2px solid ${selected?'var(--primary)':'var(--border)'}`,background:selected?'var(--primary)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontWeight:700,fontSize:12,color:selected?'white':'var(--text2)' }}>
                          {selected ? '✓' : String.fromCharCode(65+j)}
                        </span>
                        <span style={{ fontSize:15,flex:1,minWidth:0,overflowWrap:'break-word',wordBreak:'break-word' }}><MathText text={opt} /></span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ display:'flex',justifyContent:'space-between',marginTop:24,gap:8 }}>
                  <button onClick={() => setCurrentQ(c => Math.max(0,c-1))} className="btn btn-secondary" disabled={currentQ===0}>← Previous</button>
                  <button onClick={() => setCurrentQ(c => Math.min(questions.length-1,c+1))} className="btn btn-primary" disabled={currentQ===questions.length-1}>Next →</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'result') return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20,background:'radial-gradient(ellipse at 50% 0%, rgba(16,185,129,0.2) 0%, transparent 60%), var(--bg)' }}>
      <div style={{ maxWidth:480,width:'100%' }}>
        <div style={{ textAlign:'center',marginBottom:24 }}>
          <div style={{ fontSize:64,marginBottom:8 }}>{result.percentage>=80?'🏆':result.percentage>=50?'✅':'😔'}</div>
          <h1 style={{ fontSize:28,fontWeight:800 }}>Exam Submitted!</h1>
          <p style={{ color:'var(--text2)' }}>{examInfo.title}</p>
        </div>
        {result.allow_result_view ? (
          <div className="card">
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20 }}>
              {[
                { label:'Score',value:`${result.score}/${result.total_marks}`,icon:'🎯' },
                { label:'Percentage',value:`${result.percentage}%`,icon:'📊' },
                { label:'Correct',value:result.correct_count,icon:'✅' },
                { label:'Incorrect',value:result.incorrect_count,icon:'❌' },
              ].map(s => (
                <div key={s.label} className="card stat-card card-sm">
                  <div style={{ fontSize:24,marginBottom:4 }}>{s.icon}</div>
                  <div style={{ fontWeight:800,fontSize:22,color:'var(--primary-light)' }}>{s.value}</div>
                  <div style={{ fontSize:12,color:'var(--text2)' }}>{s.label}</div>
                </div>
              ))}
            </div>
            {result.allow_pdf_download && (
              <button className="btn btn-primary btn-full" onClick={downloadResultPDF}>📄 Download Result PDF</button>
            )}
          </div>
        ) : (
          <div className="card text-center">
            <div style={{ fontSize:48,marginBottom:12 }}>✅</div>
            <h3>Exam submitted successfully!</h3>
            <p className="text-muted" style={{ marginTop:8 }}>Your teacher will share the results. Check back later.</p>
          </div>
        )}
        {reviewDetails?.allow_solution_view && reviewDetails.question_details?.some(q => q.explanation) && (
          <div className="card" style={{ marginTop: 16, textAlign: 'left' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 12 }}>💡 Explanations</h3>
            {reviewDetails.question_details.map((q, i) => q.explanation ? (
              <div key={q.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: i < reviewDetails.question_details.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}><span style={{ color: 'var(--primary-light)' }}>Q{i + 1}.</span> <MathText text={q.question_text} /></div>
                <div style={{ fontSize: 13, color: 'var(--text2)' }}><MathText text={q.explanation} /></div>
              </div>
            ) : null)}
          </div>
        )}
        <div style={{ textAlign:'center',marginTop:20,fontSize:12,color:'var(--text3)' }}>
          Powered by {settings.brand_name || 'Oexams'} • {settings.tagline || ''}
        </div>
      </div>
    </div>
  );

  return null;
}
